import React from 'react';
import PathwayCard from './PathwayCard';
import SmallNavigationCards from './SmallNavigationCards';
import { Settings, Package2, Lightbulb } from 'lucide-react';

const PathwayCards = ({ onConfigureKit, onBrowseProducts, onGetGuidance, onNavigate }) => {
  const pathways = [
    {
      id: 'configure-kit',
      icon: Settings,
      title: 'Build My Turnkey Production Kit',
      subtitle: 'I know what I need.',
      description: 'Take control of your production. Build my turnkey production kit and explore how I get up and running with my own production in less than 72 hours.',
      customContent: (
        <div className="flex-grow flex flex-col">
          {/* Stylized Kit Demonstration */}
          <div className="bg-white backdrop-blur-sm rounded-lg shadow-md p-2 pt-4 pb-4 mb-7 border border-orange-200/50">
            <div className="text-center">
              <p className="text-gray-700 text-lg font-semibold mb-2">BUILD YOUR KIT:</p>
              <div className="flex items-center justify-center space-x-2">
                <span className="bg-orange-100 text-brandOrange px-3 py-1 rounded-full text-lg font-bold">Films</span>
                <span className="text-orange-400 text-lg font-bold">+</span>
                <span className="bg-orange-100 text-brandOrange px-3 py-1 rounded-full text-lg font-bold">Containers</span>
                <span className="text-orange-400 text-lg font-bold">+</span>
                <span className="bg-orange-100 text-brandOrange px-3 py-1 rounded-full text-lg font-bold leading-tight">Sealing Machines</span>
              </div>
            </div>
          </div>
          <p className="text-gray-700 text-md leading-relaxed mb-6"><span className='font-bold text-black'>Take control of your production.</span> Build your <span className='font-semibold text-brandOrange'>turnkey production kit</span> to see how you can get up and running with your own production in less than 72 hours.</p>
          
          {/* Original Bullet Points */}
          <div className="flex-grow">
            {[
              { text: 'Start by selecting the ', highlight: 'Containers', suffix: ' perfect for your product.' },
              { text: 'Then, select the ', highlight: 'Films', suffix: ' that are compatible with your product, its storage conditions, and substrate.' },
              { text: 'And finish with ', highlight: 'Sealing Machines', suffix: '' }
            ].map((bullet, index) => (
              <div key={index} className="flex items-start mb-3">
                <span className="text-gray-400 mr-3 mt-1">•</span>
                <p className="text-gray-700 text-base leading-relaxed">
                  {bullet.text}
                  {bullet.highlight && (
                    <span className="font-bold text-gray-900">{bullet.highlight}</span>
                  )}
                  {bullet.suffix}
                </p>
              </div>
            ))}
          </div>
        </div>
      ),
      bullets: [
        { text: 'Start by selecting the ', highlight: 'Containers', suffix: ' perfect for your product.' },
        { text: 'Then, select the ', highlight: 'Films', suffix: ' that are compatible with your product, its storage conditions, and substrate.' },
        { text: 'And finish with ', highlight: 'Sealing Machines', suffix: '' }
      ],
      buttonText: 'Start Building',
      onClick: onConfigureKit,
      bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
      borderColor: 'border-orange-200',
      iconColor: 'text-brandOrange'
    },
    {
      id: 'browse-products',
      icon: Package2,
      title: 'Browse Products',
      subtitle: 'I just want to explore.',
      description: 'Browse Teinnovations product catalog at your own pace. Click on a category below to get started.',
      customContent: (
        <div className="flex-grow flex flex-col">
          {/* Description */}
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
          Browse Teinnovations' product catalog at your own pace. Click on a category below to get started.
          </p>
          
          {/* Navigation Cards */}
          <div className="flex-grow">
            <SmallNavigationCards onNavigate={onNavigate} />
          </div>
        </div>
      ),
      bullets: [
        { text: 'Containers', highlight: '', suffix: '' },
        { text: 'Films', highlight: '', suffix: '' },
        { text: 'Sealing Machines', highlight: '', suffix: '' }
      ],
      buttonText: 'Start Browsing',
      onClick: onBrowseProducts,
      bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      id: 'guidance',
      icon: Lightbulb,
      title: 'Not Sure Where to Start?',
      subtitle: '',
      description: 'Answer a few quick questions and we\'ll line up the right containers and sealing films for you.',
      customContent: (
        <div className="flex-grow flex flex-col">
          {/* Description */}
          <p className="text-gray-700 text-lg leading-relaxed mb-0">
            Find the perfect packaging for your product in just a few easy steps.
          </p>
          
          {/* Bullet Points 
          <div className="flex-grow">
            {[
              { text: 'Product type & portion size', highlight: '', suffix: '' },
              { text: 'Compartments & heating needs', highlight: '', suffix: '' },
              { text: 'Film compatibility & finishing touches', highlight: '', suffix: '' }
            ].map((bullet, index) => (
              <div key={index} className="flex items-start mb-3">
                <span className="text-gray-400 mr-3 mt-1">•</span>
                <p className="text-gray-700 text-base leading-relaxed">
                  {bullet.text}
                  {bullet.highlight && (
                    <span className="font-bold text-gray-900">{bullet.highlight}</span>
                  )}
                  {bullet.suffix}
                </p>
              </div>
            ))}
          </div>*/}
        </div>
      ),
      bullets: [
        { text: 'Product type & portion size', highlight: '', suffix: '' },
        { text: 'Compartments & heating needs', highlight: '', suffix: '' },
        { text: 'Film compatibility & finishing touches', highlight: '', suffix: '' }
      ],
      buttonText: 'Guide Me',
      onClick: onGetGuidance,
      bgColor: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
      borderColor: 'border-yellow-200',
      iconColor: 'text-yellow-600'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* First row: Not Sure Where to Start */}
      <div className="w-full mb-8">
        <PathwayCard {...pathways[2]} />
      </div>
      
      {/* Second row: Configure My Kit and Browse Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
        {pathways.slice(0, 2).map((pathway) => (
          <PathwayCard key={pathway.id} {...pathway} />
        ))}
      </div>
    </div>
  );
};

export default PathwayCards;
